import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Avatar, Button, Chip, IconButton, InputAdornment, LinearProgress, ListItemIcon, Menu, MenuItem, Select, TextField, Tooltip } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Skeleton from "@material-ui/lab/Skeleton";
import { Add, ArrowDropDown, Block, ChatBubbleOutline, ChevronLeft, ChevronRight, CloudUploadOutlined, ContactPhoneOutlined, DeleteOutline, EditOutlined, ErrorOutline, Facebook, History, ImportExport, Instagram, MoreHoriz, PeopleOutline, Refresh, Search, WhatsApp } from "@material-ui/icons";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { Can } from "../../components/Can";
import ContactModal from "../../components/ContactModal";
import NewTicketModal from "../../components/NewTicketModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContactImportWpModal from "../../components/ContactImportWpModal";
import { contactPresentation } from "../CrmRelationship/helpers";
import ContactsTheme from "./theme";
import useStyles from "./styles";
import useContacts from "./useContacts";

function channelInfo(channel) {
  if (channel === "instagram") return { label: "Instagram", Icon: Instagram };
  if (channel === "facebook") return { label: "Facebook", Icon: Facebook };
  if (channel === "whatsapp") return { label: "WhatsApp", Icon: WhatsApp };
  return { label: "Contato", Icon: ChatBubbleOutline };
}

function ContactsPage() {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const list = useContacts({ companyId: user.companyId, socket });
  const [contactEditor, setContactEditor] = useState(null);
  const [ticketContact, setTicketContact] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [importMenu, setImportMenu] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [privacy, setPrivacy] = useState({ enabled: true, hide: true });
  const hiddenNumber = user.profile === "user" && privacy.enabled && privacy.hide;

  useEffect(() => {
    let active = true;
    api.get("/tags/list").then(({ data }) => { if (active) setTagOptions(data); }).catch(toastError);
    api.get(`/companySettings/${user.companyId}`).then(({ data }) => {
      if (active) setPrivacy({ enabled: data.enableLGPD === "enabled", hide: data.lgpdHideNumber === "enabled" });
    }).catch(toastError);
    return () => { active = false; };
  }, [user.companyId]);

  const closeEditor = () => { setContactEditor(null); list.refresh(); };
  const closeTicket = ticket => {
    setTicketContact(null);
    if (ticket && ticket.uuid) {
      setCurrentTicket({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
      history.push(`/tickets/${ticket.uuid}`);
    }
  };
  const confirmAction = async () => {
    if (!confirmation || actionBusy) return;
    const { type, contact } = confirmation;
    setActionBusy(true);
    try {
      if (type === "delete") {
        await api.delete(`/contacts/${contact.id}`);
        toast.success(i18n.t("contacts.toasts.deleted"));
      } else if (type === "block") {
        await api.put(`/contacts/block/${contact.id}`, { active: !contact.active });
        toast.success(contact.active ? "Contato bloqueado" : "Contato desbloqueado");
      } else if (type === "import") {
        await api.post("/contacts/import");
        toast.success("Contatos importados");
      }
      list.refresh();
    } catch (err) { toastError(err); }
    finally { setActionBusy(false); }
  };
  const requestAction = (type, contact) => { setRowMenu(null); setConfirmation({ type, contact }); };
  const confirmedName = contactPresentation(confirmation?.contact, hiddenNumber).name;
  const confirmTitle = confirmation?.type === "delete" ? `Excluir ${confirmedName}?`
    : confirmation?.type === "block" ? `${confirmation.contact.active ? "Bloquear" : "Desbloquear"} ${confirmedName}?`
    : i18n.t("contacts.confirmationModal.importTitlte");
  const confirmMessage = confirmation?.type === "delete" ? i18n.t("contacts.confirmationModal.deleteMessage")
    : confirmation?.type === "block" ? i18n.t(`contacts.confirmationModal.${confirmation.contact.active ? "blockContact" : "unblockContact"}`)
    : i18n.t("contacts.confirmationModal.importMessage");
  const rangeStart = list.contacts.length ? (list.pageNumber - 1) * list.pageSize + 1 : 0;
  const rangeEnd = list.contacts.length ? rangeStart + list.contacts.length - 1 : 0;

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <div className={classes.titleRow}>
          <span className={classes.titleIcon}><PeopleOutline /></span>
          <h1 className={classes.title}>Contatos</h1>
          {!list.loading && !list.error && <span className={classes.count}>{list.count.toLocaleString("pt-BR")}</span>}
        </div>
        <div className={classes.headerActions}>
          <Button variant="outlined" className={classes.secondary} startIcon={<ImportExport />} endIcon={<ArrowDropDown />} onClick={event => setImportMenu(event.currentTarget)} disabled={actionBusy}>
            Importar / Exportar
          </Button>
          <Button variant="contained" className={classes.primary} startIcon={<Add />} onClick={() => setContactEditor({ id: null })}>Novo contato</Button>
        </div>
      </header>

      <section className={classes.panel} aria-label="Lista de contatos" aria-busy={list.loading}>
        <div className={classes.filters}>
          <TextField className={classes.search} size="small" variant="outlined" type="search" placeholder="Buscar por nome ou telefone" value={list.search} onChange={event => list.setSearch(event.target.value)} inputProps={{ "aria-label": "Buscar por nome ou telefone" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <Autocomplete className={classes.tagsFilter} multiple size="small" options={tagOptions} value={selectedTags} getOptionLabel={tag => tag.name} getOptionSelected={(a, b) => a.id === b.id}
            onChange={(_, tags) => { setSelectedTags(tags); list.setTags(tags.map(tag => tag.id)); }}
            renderTags={(tags, getProps) => tags.map((tag, index) => <Chip size="small" label={tag.name} {...getProps({ index })} />)}
            renderInput={params => <TextField {...params} variant="outlined" placeholder={selectedTags.length ? "" : "Etiquetas"} inputProps={{ ...params.inputProps, "aria-label": "Filtrar por etiquetas" }} />} />
          <Tooltip title="Atualizar contatos"><span><IconButton size="small" aria-label="Atualizar contatos" onClick={list.refresh} disabled={list.loading}><Refresh /></IconButton></span></Tooltip>
        </div>
        <div className={classes.progress}>{(list.loading || actionBusy) && <LinearProgress />}</div>

        <div className={classes.content} key={`${list.pageNumber}-${list.pageSize}-${list.searchParam}-${list.tags.join(",")}`}>
        {list.error ? <div className={classes.empty} role="alert"><ErrorOutline /><p>Não foi possível carregar os contatos.</p><Button variant="outlined" onClick={list.refresh} startIcon={<Refresh />}>Tentar novamente</Button></div>
          : !list.contacts.length ? <div className={classes.empty}>
            {list.loading ? <><Skeleton width="80%" height={40} /><Skeleton width="80%" height={40} /><Skeleton width="80%" height={40} /></>
              : <><PeopleOutline /><p>{list.searchParam || list.tags.length ? "Nenhum contato encontrado." : "Nenhum contato cadastrado."}</p>{!list.searchParam && !list.tags.length && <Button className={classes.secondary} variant="outlined" startIcon={<Add />} onClick={() => setContactEditor({ id: null })}>Novo contato</Button>}</>}
          </div>
          : <>
            <div className={`${classes.row} ${classes.rowHeader}`} aria-hidden="true"><span>Contato</span><span>Etiquetas</span><span className={classes.channel}>Canal</span><span>Status</span><span style={{ textAlign: "right" }}>Ações</span></div>
            <div>
              {list.contacts.map(contact => {
                const { label, Icon } = channelInfo(contact.channel);
                const tags = contact.tags || [];
                const person = contactPresentation(contact, hiddenNumber);
                return <article key={contact.id} className={classes.row} aria-label={person.name}>
                  <div className={classes.contact}>
                    <Avatar className={classes.avatar} src={contact.urlPicture || undefined}>{person.initials}</Avatar>
                    <div className={classes.contactText}>
                      <span className={classes.name} title={person.name}>{person.name}</span>
                      {(person.phone || contact.isGroup) && <div className={classes.detail}>{contact.isGroup ? "Grupo" : person.phone}</div>}
                      {person.email && <div className={classes.detail} title={person.email}>{person.email}</div>}
                      {!person.hasName && <div className={classes.detail}>{person.reference}</div>}
                    </div>
                  </div>
                  <div className={`${classes.tagList} ${!tags.length ? classes.noTags : ""}`}>
                    {tags.slice(0, 2).map(tag => <Chip key={tag.id} className={classes.tag} size="small" label={tag.name} title={tag.name} />)}
                    {tags.length > 2 && <Tooltip title={tags.slice(2).map(tag => tag.name).join(", ")}><Chip className={classes.tag} size="small" label={`+${tags.length - 2}`} /></Tooltip>}
                    {!tags.length && <span className={classes.detail}>—</span>}
                  </div>
                  <div className={classes.channel} title={label}><Icon /><span>{label}</span></div>
                  <span className={`${classes.status} ${!contact.active ? classes.blocked : ""}`}>{contact.active ? "Ativo" : "Bloqueado"}</span>
                  <div className={classes.actions}>
                    <Tooltip title="Iniciar conversa"><span><IconButton className={`${classes.action} ${classes.start}`} aria-label={`Iniciar conversa com ${person.name}`} disabled={!contact.active} onClick={() => setTicketContact(contact)}><Icon /></IconButton></span></Tooltip>
                    <Tooltip title="Ver relacionamento"><IconButton className={classes.action} aria-label={`Ver relacionamento de ${person.name}`} onClick={() => history.push(`/crm/relationship/${contact.id}`)}><History /></IconButton></Tooltip>
                    <Tooltip title="Editar contato"><IconButton className={classes.action} aria-label={`Editar ${person.name}`} onClick={() => setContactEditor(contact)}><EditOutlined /></IconButton></Tooltip>
                    <Tooltip title="Mais ações"><IconButton className={classes.action} aria-label={`Mais ações para ${person.name}`} disabled={actionBusy} onClick={event => setRowMenu({ anchor: event.currentTarget, contact })}><MoreHoriz /></IconButton></Tooltip>
                  </div>
                </article>;
              })}
            </div>
          </>}
        </div>

        <footer className={classes.footer}>
          <div className={classes.pageSize}><span>Por página</span><Select className={classes.sizeSelect} variant="outlined" value={list.pageSize} onChange={event => list.setPageSize(Number(event.target.value))} inputProps={{ "aria-label": "Contatos por página" }}>
            {[25, 50, 100].map(size => <MenuItem key={size} value={size}>{size}</MenuItem>)}
          </Select></div>
          <div className={classes.pagination}>
            <span className={classes.range} aria-live="polite">{list.loading ? "Carregando…" : list.error ? "—" : `${rangeStart}–${rangeEnd} de ${list.count.toLocaleString("pt-BR")}`}</span>
            <IconButton size="small" aria-label="Página anterior" disabled={list.loading || list.pageNumber <= 1} onClick={() => list.setPage(list.pageNumber - 1)}><ChevronLeft /></IconButton>
            <span>Página {list.pageNumber}</span>
            <IconButton size="small" aria-label="Próxima página" disabled={list.loading || list.error || !list.hasMore} onClick={() => list.setPage(list.pageNumber + 1)}><ChevronRight /></IconButton>
          </div>
        </footer>
      </section>

      <Menu anchorEl={importMenu} open={Boolean(importMenu)} onClose={() => setImportMenu(null)}>
        <MenuItem onClick={() => { setImportMenu(null); requestAction("import"); }}><ListItemIcon className={classes.menuIcon}><ContactPhoneOutlined /></ListItemIcon>{i18n.t("contacts.menu.importYourPhone")}</MenuItem>
        <MenuItem onClick={() => { setImportMenu(null); setImportOpen(true); }}><ListItemIcon className={classes.menuIcon}><CloudUploadOutlined /></ListItemIcon>Planilha Excel</MenuItem>
      </Menu>
      <Menu anchorEl={rowMenu?.anchor || null} open={Boolean(rowMenu)} onClose={() => setRowMenu(null)}>
        <MenuItem onClick={() => { setContactEditor(rowMenu.contact); setRowMenu(null); }}><ListItemIcon className={classes.menuIcon}><EditOutlined /></ListItemIcon>Dados do cliente</MenuItem>
        <MenuItem onClick={() => requestAction("block", rowMenu.contact)}><ListItemIcon className={classes.menuIcon}><Block /></ListItemIcon>{rowMenu?.contact.active ? "Bloquear contato" : "Desbloquear contato"}</MenuItem>
        <Can role={user.profile} perform="contacts-page:deleteContact" yes={() => <MenuItem className={classes.danger} onClick={() => requestAction("delete", rowMenu.contact)}><ListItemIcon className={classes.menuIcon}><DeleteOutline /></ListItemIcon>Excluir contato</MenuItem>} />
      </Menu>
      <ConfirmationModal open={Boolean(confirmation)} title={confirmTitle} onClose={() => setConfirmation(null)} onConfirm={confirmAction}>{confirmMessage}</ConfirmationModal>
      {contactEditor && <ContactModal open contactId={contactEditor.id} onClose={closeEditor} />}
      {ticketContact && <NewTicketModal modalOpen initialContact={ticketContact} onClose={closeTicket} />}
      {importOpen && <ContactImportWpModal isOpen handleClose={() => { setImportOpen(false); list.refresh(); }} selectedTags={list.tags} hideNum={hiddenNumber} userProfile={user.profile} />}
    </div>
  );
}

export default function Contacts() {
  const { user } = useContext(AuthContext);
  return <ContactsTheme><ContactsPage key={`${user.companyId}-${user.id}`} /></ContactsTheme>;
}
